import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import SalaryStructureForm from "./SalaryStructureForm.jsx";
import { formatDate } from "../../../../lib/employeeHelpers";
import AdvancedFormulaManager from "./AdvancedFormulaManager.jsx";
import SalaryComponentsPanel from "./SalaryComponentsPanel.jsx";
import { salaryStructureService } from "../../../../services/api";
import {
  structureToTemplatePayload,
  templateToStructure,
} from "./utils/salaryStructurePersistence";
import { findConflictingSalaryStructure } from "../../../../utils/salaryStructureMatch";
import {
  rematchStructureComponentsToLibrary,
  extractFormulasFromStructures,
  loadMergedFormulasFromStorage,
  persistFormulasToStorage,
} from "./utils/salaryComponentLibrarySync";
import { DsButton } from "../../../../design-system";

const SalaryStructureManager = ({
  salaryComponents,
  onComponentUpdate,
  organizationalLevels = {},
  statutoryRates = {},
  statutoryConfigJson = null,
  components = [],
  onSaveComponents,
  onCreateComponent,
  onUpdateComponent,
  onDeleteComponent,
  onSyncLibraryFromStructures,
  darkMode = false,
}) => {
  const [structures, setStructures] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showFormulaManager, setShowFormulaManager] = useState(false);

  const syncLibraryRef = useRef(onSyncLibraryFromStructures);
  syncLibraryRef.current = onSyncLibraryFromStructures;

  // Single source of truth: created library components (active only)
  const libraryComponents = (components?.length ? components : salaryComponents || []).filter(
    (c) => c?.isActive !== false && c?.active !== false
  );
  const loading = false;

  const loadStructures = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await salaryStructureService.listTemplates();
      const rows = (Array.isArray(res?.data) ? res.data : [])
        .map(templateToStructure)
        .filter(Boolean);
      setStructures(rows);

      // Ensure every structure component exists in the library panel
      if (typeof syncLibraryRef.current === "function" && rows.length) {
        await syncLibraryRef.current(rows);
      }

      // Pull formulas embedded in structures into Formula Manager storage
      const fromStructures = extractFormulasFromStructures(rows);
      if (fromStructures.length) {
        const existing = loadMergedFormulasFromStorage();
        const byId = new Map(existing.map((f) => [String(f.id), f]));
        fromStructures.forEach((f) => {
          if (!byId.has(String(f.id))) byId.set(String(f.id), f);
        });
        persistFormulasToStorage(Array.from(byId.values()));
      }
    } catch (err) {
      console.error("Failed to load salary structures from DB", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to load salary structures (backend/API). Existing list kept if already loaded."
      );
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStructures();
  }, [loadStructures]);

  const handleCreateStructure = async () => {
    if (onComponentUpdate) {
      try {
        await onComponentUpdate();
      } catch {
        /* ignore refresh errors */
      }
    }
    setEditingStructure(null);
    setShowCreateForm(true);
  };

  const handleEditStructure = (structure) => {
    const rematched = {
      ...structure,
      components: rematchStructureComponentsToLibrary(
        structure.components || [],
        libraryComponents
      ),
    };
    setEditingStructure(rematched);
    setShowCreateForm(true);
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setEditingStructure(null);
  };

  const handleSaveStructure = async (structureData) => {
    const payload = structureToTemplatePayload(structureData);
    if (!payload.name) {
      toast.error("Structure name is required");
      return;
    }

    const conflict = findConflictingSalaryStructure(
      structureData.assignment || {},
      structures,
      editingStructure?.id
    );
    if (conflict) {
      toast.error(
        `Applicability overlaps with structure "${conflict.name}". One employee group can have only one active salary structure.`,
        { duration: 7000 }
      );
      return;
    }

    setSaving(true);
    try {
      if (editingStructure?.id != null) {
        const res = await salaryStructureService.updateTemplate(editingStructure.id, {
          ...payload,
          active: editingStructure.active !== false,
        });
        const updated = templateToStructure(res?.data);
        setStructures((prev) =>
          prev.map((s) => (s.id === editingStructure.id ? updated || s : s))
        );
        toast.success("Salary structure updated");
        if (res?.reimbSync?.started) {
          toast("Employee structures syncing in the background…", { icon: "ℹ️" });
        } else if (
          res?.reimbSync?.reason === "FINALIZED_PAYROLL_MONTH_LOCK" ||
          res?.reimbSync?.reason === "finalized_month_lock" ||
          res?.reimbSync?.reason === "STRUCTURE_REVISION_MONTH_LOCKED"
        ) {
          toast.error(
            res.reimbSync.message ||
              "Cannot sync: Status from is in a finalised/locked payroll month. Change Status from, or unprocess/unfinalise that month first."
          );
        } else if (res?.reimbSync?.reason === "missing_status_from") {
          toast.error(res.reimbSync.message || "Set Status from on reimbursement components.");
        } else if (res?.reimbSync?.reason === "already_synced") {
          toast.success("Assigned employees already have these reimbursements");
        }
      } else {
        const res = await salaryStructureService.createTemplate(payload);
        const created = templateToStructure(res?.data);
        if (created) setStructures((prev) => [created, ...prev]);
        toast.success("Salary structure saved");
        if (res?.reimbSync?.started) {
          toast("Employee structures syncing in the background…", { icon: "ℹ️" });
        }
      }
      setShowCreateForm(false);
      setEditingStructure(null);
    } catch (err) {
      console.error("Failed to save salary structure", err);
      toast.error(err?.response?.data?.message || "Failed to save salary structure");
    } finally {
      setSaving(false);
    }
  };

  const handleMenuToggle = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleMenuAction = async (action, id, name) => {
    setOpenMenuId(null);
    const current = structures.find((s) => s.id === id);
    if (!current) return;

    try {
      switch (action) {
        case "edit": {
          handleEditStructure(current);
          break;
        }
        case "deactivate": {
          if (!window.confirm(`Deactivate "${name}"?`)) return;
          const res = await salaryStructureService.updateTemplate(id, { active: false });
          const updated = templateToStructure(res?.data) || { ...current, active: false };
          setStructures((prev) => prev.map((s) => (s.id === id ? updated : s)));
          toast.success("Structure deactivated");
          break;
        }
        case "reactivate": {
          if (!window.confirm(`Reactivate "${name}"?`)) return;
          const res = await salaryStructureService.updateTemplate(id, { active: true });
          const updated = templateToStructure(res?.data) || { ...current, active: true };
          setStructures((prev) => prev.map((s) => (s.id === id ? updated : s)));
          toast.success("Structure reactivated");
          break;
        }
        case "delete": {
          if (
            !window.confirm(
              `Permanently delete "${name}" from the database? This cannot be undone.`
            )
          ) {
            return;
          }
          await salaryStructureService.deleteTemplate(id);
          setStructures((prev) => prev.filter((s) => s.id !== id));
          toast.success("Structure deleted");
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error("Structure action failed", err);
      toast.error(err?.response?.data?.message || "Action failed");
    }
  };

  const filteredStructures = showDeactivated
    ? structures
    : structures.filter((structure) => structure.active !== false);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
      <div className="space-y-6 xl:col-span-3">
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="ds-h3 mb-1 text-[hsl(var(--ds-fg))]">Salary Structures</h2>
              <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                Saved to the company database (salary structure templates)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <DsButton
                type="button"
                variant={showDeactivated ? "secondary" : "outline"}
                size="sm"
                onClick={() => setShowDeactivated(!showDeactivated)}
              >
                {showDeactivated ? "Hide Inactive" : "Show Inactive"}
              </DsButton>

              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowFormulaManager(true)}
              >
                Formula Manager
              </DsButton>

              <DsButton type="button" variant="primary" size="sm" onClick={handleCreateStructure}>
                Create Structure
              </DsButton>
            </div>
          </div>

          {listLoading ? (
            <div className="py-12 text-center text-sm text-[hsl(var(--ds-muted-fg))]">Loading structures…</div>
          ) : filteredStructures.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[hsl(var(--ds-border))] rounded-xl space-y-3">
              <p className={`text-sm ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                No salary structures for this company yet. Click Create Structure to add one.
              </p>
              <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                Structures are loaded from the database for your logged-in company. If one vanished
                after a refresh, try Reload (API may have timed out while the backend was restarting).
              </p>
              <button
                type="button"
                onClick={loadStructures}
                className="inline-flex items-center px-3 py-1.5 border border-[hsl(var(--ds-border))] text-xs font-medium rounded-md text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
              >
                Reload
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredStructures.map((structure) => (
                <div
                  key={structure.id}
                  className={`rounded-xl border px-4 py-3 flex items-center justify-between ${
                    structure.active !== false
                      ? darkMode
                        ? "border-[hsl(var(--ds-border))] bg-[#2C2C2C]"
                        : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                      : darkMode
                        ? "border-[hsl(var(--ds-border))] opacity-70"
                        : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] opacity-75"
                  }`}
                >
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold truncate ${
                        darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {structure.name}
                    </p>
                    <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      {(structure.salaryType || "gross").toUpperCase()} ·{" "}
                      {(structure.components || []).length} components
                      {structure.createdAt ? ` · ${formatDate(structure.createdAt)}` : ""}
                      {structure.active === false ? " · Inactive" : ""}
                    </p>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => handleMenuToggle(structure.id)}
                      className={`rounded-lg p-1.5 ${
                        darkMode
                          ? "hover:bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                          : "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                      }`}
                      aria-label="Structure actions"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                    {openMenuId === structure.id && (
                      <div
                        className={`absolute right-0 top-8 z-20 min-w-[9rem] overflow-hidden rounded-xl border shadow-lg ${
                          darkMode
                            ? "border-[hsl(var(--ds-border))] bg-[#1a1a1a]"
                            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleMenuAction("edit", structure.id, structure.name)}
                          className={`block w-full px-3 py-2 text-left text-xs ${
                            darkMode
                              ? "text-gray-200 hover:bg-[#2C2C2C]"
                              : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                          }`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleMenuAction(
                              structure.active !== false ? "deactivate" : "reactivate",
                              structure.id,
                              structure.name
                            )
                          }
                          className={`block w-full px-3 py-2 text-left text-xs ${
                            darkMode
                              ? "text-gray-200 hover:bg-[#2C2C2C]"
                              : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                          }`}
                        >
                          {structure.active !== false ? "Deactivate" : "Reactivate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMenuAction("delete", structure.id, structure.name)}
                          className={`block w-full px-3 py-2 text-left text-xs ${
                            darkMode
                              ? "text-red-400 hover:bg-[#2C2C2C]"
                              : "text-red-600 hover:bg-red-50"
                          }`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreateForm && (
        <SalaryStructureForm
          isOpen={showCreateForm}
          onClose={handleCloseForm}
          onSubmit={handleSaveStructure}
          savedComponents={libraryComponents}
          loading={loading || saving}
          editingStructure={editingStructure}
          organizationalLevels={organizationalLevels}
          statutoryRates={statutoryRates}
          statutoryConfigJson={statutoryConfigJson}
          structures={structures}
        />
      )}

      {showFormulaManager && (
        <AdvancedFormulaManager
          isOpen={showFormulaManager}
          onClose={() => setShowFormulaManager(false)}
          savedComponents={libraryComponents}
          structures={structures}
          organizationalLevels={organizationalLevels}
          statutoryRates={statutoryRates}
        />
      )}

      <div className="xl:col-span-2">
        <SalaryComponentsPanel
          components={components}
          onSaveComponents={onSaveComponents}
          onCreateComponent={onCreateComponent}
          onUpdateComponent={onUpdateComponent}
          onDeleteComponent={onDeleteComponent}
          darkMode={darkMode}
        />
      </div>
    </div>
  );
};

export default SalaryStructureManager;
